import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
    ArrowLeft,
    FileText,
    DollarSign, Download,
    Eye,
    Play,
    Trash2,
    AlertCircle,
    Upload, Clock,
    CheckCircle,
    XCircle,
    Send,
    Loader2
} from 'lucide-react';
import Layout from '../components/Layout';
import Button from '../components/Button';
import Modal from '../components/Modal';
import LoadingSpinner from '../components/LoadingSpinner';
import { useClaimStore } from '../store/claimStore';
import { useUIStore } from '../store/uiStore';
import { useScreenNavigation } from '../hooks/useScreenNavigation';
import { ClaimStatus } from '../types';
import type { DocumentCategory, SubmittedFileEntry } from '../types';
import { formatDateOrNA } from '../utils/dateUtils';
import { Country, getCountryLabel } from '../types/Country.enum';
import { downloadDocument } from '../services/api/endpoints/documentEndpoints';
import { logger } from '../utils/logger';
import { getCoverageCodeLabel } from '../data/coverageCodes';
import { categoryFromScreenCode } from '../utils/documentCategoryMapper';
import { resolveDocumentTypeLabel } from '../utils/documentTypeMapper';
import PreviouslySubmittedExpenses from './components/expense-validation/PreviouslySubmittedExpenses';
import ClaimDocumentsTab from './components/claim-details/ClaimDocumentsTab';

const SCREEN_CODE = '00-10-RECLAMATIONS';

/**
 * Map a NOVA Payment_Decision value to its display badge.
 *
 * NOVA emits APPROVED / REFUSED / PENDING. The value is normalized (trimmed +
 * upper-cased) so casing drift can't silently fall through, and any unknown or
 * blank decision is treated as "pending" rather than "refused" — a payment that
 * has not been refused must never be shown to the client as denied.
 */
const getPaymentDecisionBadge = (decision?: string): { labelKey: string; className: string } => {
    const pending = { labelKey: 'claimDetails.payments.pending', className: 'bg-amber-100 text-amber-800' };
    switch ((decision ?? '').trim().toUpperCase()) {
        case 'APPROVED':
            return { labelKey: 'claimDetails.payments.approved', className: 'bg-green-100 text-green-800' };
        case 'REFUSED':
            return { labelKey: 'claimDetails.payments.denied', className: 'bg-red-100 text-red-800' };
        case 'PENDING':
            return pending;
        default:
            // Blank or unexpected NOVA decision — surface possible upstream drift, then
            // fall back to pending (never a misleading "refused").
            logger.warn('Unexpected payment decision value; defaulting to pending', { decision });
            return pending;
    }
};

/**
 * DEV-6896 — A raw document entry as it lives inside `stepData[X].documents`.
 * Field shape varies across upload screens (legacy drafts only carry
 * `fileName`/`fileType`/`mimeType`/`uploadDate`; the BFF-supplied list emits the
 * canonical Portal names: `name`/`type`/`uploadedAt`/`category`). Treated as a
 * loose record here; `toSubmittedFileEntry` normalizes it into a typed
 * {@link SubmittedFileEntry}.
 */
type RawStepDocument = Record<string, unknown>;

/**
 * DEV-6896 — Normalize a raw document entry from `stepData` into the typed
 * {@link SubmittedFileEntry} shape consumed by `ClaimDocumentsTab`.
 *
 * Fallback chain mirrors the historical fields seen in the codebase:
 * - canonical Portal names first (`name`/`type`/`uploadedAt`),
 * - legacy upload-screen field names next (`fileName`/`fileType`/`mimeType`/`uploadDate`),
 * - last-resort defaults (`'Document'`/`'Unknown'`/now()) so a partial payload still renders.
 *
 * The `category` parameter is provided by the caller — submitted claims pull it
 * from the BFF list; drafts derive it from the upload screen code.
 */
function toSubmittedFileEntry(
  doc: RawStepDocument,
  index: number,
  category: DocumentCategory,
): SubmittedFileEntry {
  // DEV-6974 — Pick a DocMana DEV-6164 subtype off whichever field happens to carry
  // it on this entry, so the documents tab can sub-group correctly on BOTH paths:
  //   - SUBMITTED: BFF emits `doc.type` already populated with the canonical subtype.
  //   - DRAFT: upload screens stash the subtype under `doc.documentTypes[0]` (array
  //     for multi-page), `doc.aiResult.documentType` (legacy single-page), or
  //     `doc.documentType` (SupportingDocumentsScreen file shape).
  // Validate each candidate against the catalog so sentinel strings (`'AUTRE'`,
  // `'UNCLASSIFIED'`, MIME types) don't get promoted — those fall through to the
  // UNKNOWN bucket via the legacy `doc.type` / MIME chain.
  const candidates: string[] = [];
  if (Array.isArray(doc.documentTypes) && typeof doc.documentTypes[0] === 'string') {
    candidates.push(doc.documentTypes[0]);
  }
  const ai = doc.aiResult as { documentType?: string } | undefined;
  if (typeof ai?.documentType === 'string') candidates.push(ai.documentType);
  if (typeof doc.documentType === 'string') candidates.push(doc.documentType);
  if (typeof doc.type === 'string') candidates.push(doc.type);
  const validatedSubtype = candidates.find((s) => resolveDocumentTypeLabel(s).isKnown);

  return {
    documentKey: (doc.documentKey as string) || (doc.id as string) || String(index + 1),
    name: (doc.name as string) || (doc.fileName as string) || 'Document',
    type:
      validatedSubtype ||
      (doc.type as string) ||
      (doc.fileType as string) ||
      (doc.mimeType as string) ||
      'Unknown',
    uploadedAt: (doc.uploadedAt as string) || (doc.uploadDate as string) || new Date().toISOString(),
    category,
  };
}

const ClaimDetailsScreen: React.FC = () => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const claimId = searchParams.get('id');
  const { currentClaim, stepData, getCurrentClaimType, eventNumber, currentScreenCode, loadReferenceClaim } = useClaimStore();
  useScreenNavigation(SCREEN_CODE);

  const [activeTab, setActiveTab] = useState<'overview' | 'expenses' | 'documents' | 'timeline' | 'payments'>('overview');
  const [showAbandonModal, setShowAbandonModal] = useState(false);
  const [isLoadingData, setIsLoadingData] = useState(true);
  const [isAbandoning, setIsAbandoning] = useState(false);
  const [showDownloadModal, setShowDownloadModal] = useState(false);
  const [downloadProgress, setDownloadProgress] = useState({ fileName: '', status: 'downloading' as 'downloading' | 'success' | 'error', message: '' });

  // Extract real data from store
  const claimType = getCurrentClaimType() || 'MEDICAL';
  const claimTypeLower = claimType.toLowerCase();
  const isMedical = claimTypeLower === 'medical';
  const isNonMedical = claimTypeLower === 'cancellation' || claimTypeLower === 'baggage';

  // DEBUG: Log stepData to see what's available
  // Extract beneficiary data from stepData
  const beneficiaryData = stepData['02M-01-BENEFICIAIRE'] || stepData['02A-01-BENEFICIAIRE'] || stepData['02B-01-BENEFICIAIRE'] || {};
  const beneficiaries = beneficiaryData.beneficiaries || [];
  const firstBeneficiary = beneficiaries.find((b: any) => b.selected) || beneficiaries[0] || {};
  // Extract travel data (nested in travelData object)
  const travelScreenData = stepData['02M-02-VOYAGE'] || stepData['02A-02-VOYAGE'] || stepData['02B-02-VOYAGE'] || {};
  const travelData = travelScreenData.travelData || travelScreenData;

  // Extract event details (nested in eventData object)
  const eventScreenData = stepData['03M-01-DETAILS'] || stepData['03A-01-DETAILS1'] || stepData['03B-01-DETAILS'] || {};
  const eventData = eventScreenData.eventData || eventScreenData;

  // Extract payments data
  const claimDetailsScreenData = stepData['00-10-RECLAMATIONS'] || {};
  const paymentsData = claimDetailsScreenData.payments || [];
  // DEV-7011 — BFF can stop supplying EOBs entirely via `app.features.eob.enabled=false`.
  // Default-on for backward compat: claims served by an older BFF (no flag in response) keep the column.
  const eobDisplayEnabled = claimDetailsScreenData.eobDisplayEnabled !== false;

  // Extract expenses data from validation screen (has more details than upload screen)
  const validationData = stepData['04-02-VALIDATION'] || {};
  const expensesList = validationData.expenses || [];
  const totalAmount = validationData.totalAmountCAD || 0;

  // Calculate total from expenses if not available in validation
  const calculatedTotal = expensesList.reduce((sum: number, expense: any) => {
    return sum + (expense.amountCAD || expense.amount || 0);
  }, 0);

  // Extract credits/refund amount from validation screen (calculated by ExpenseValidationScreen for all workflows)
  const creditsAmount = validationData.refundReceivedAmount || 0;

  // DEV-6896 — Documents tab data sources, **status-based branching**:
  //   * Claims that went through `Submit` (status NOT in {DRAFT, CANCELLED}) consume
  //     the BFF list ONLY — `stepData['00-10-RECLAMATIONS'].documents`, populated
  //     by `NovaToPortalMapper.mapFullExport` from NOVA's `Submitted_File`
  //     form_objects. Each entry already carries `{ documentKey, name, type,
  //     uploadedAt, category }`. If the list is absent or empty (degenerate case —
  //     NOVA bug), the tab shows the empty state. **No fallback to per-screen
  //     aggregation** — silently surfacing the buggy reconstructed
  //     Invoice/File_Reference data would mask the NOVA issue.
  //   * Claims still in DRAFT or CANCELLED (= abandoned drafts, never exported)
  //     build the list from per-screen `stepData[X].documents` and derive
  //     `category` from the screen code via `categoryFromScreenCode`.
  //
  // CANCELLED is the frontend display value for the backend ABANDONED status
  // (F03.10 in the features tree); these are drafts that were never exported.
  // Comparison stays case-insensitive as defense-in-depth, though ClaimStatus is
  // now uppercase-canonical on both sides (DEV-7449): the BFF emits "DRAFT" from
  // Portal_Submission_Status__c and normalizes Case.Status via ClaimStatus.fromCaseStatus.
  // ABANDONED is included defensively — getClaim does NOT remap it to CANCELLED
  // (only getClaimList does), so an ABANDONED submission opened directly via
  // detail would otherwise be misclassified as submitted and read from the
  // empty NOVA-export list.
  const NON_SUBMITTED_STATUSES = ['DRAFT', 'CANCELLED', 'ABANDONED'];
  const isClaimSubmitted =
    currentClaim?.status != null &&
    !NON_SUBMITTED_STATUSES.includes(currentClaim.status.toString().toUpperCase());

  // BFF-supplied list (consumed ONLY when isClaimSubmitted).
  // `stepData` is `{[k: string]: any}` upstream — `RawStepDocument` (defined at
  // module scope above) typifies the loose document records.
  const submittedDocsList: RawStepDocument[] =
    (stepData['00-10-RECLAMATIONS']?.documents as RawStepDocument[]) || [];

  // Per-screen draft sources (consumed ONLY when !isClaimSubmitted).
  const invoiceDocsList: RawStepDocument[] =
    (stepData['04-01-FACTURES']?.documents as RawStepDocument[]) || [];
  const appendInvoiceDocsList: RawStepDocument[] =
    (stepData['04E-01-FACTURES']?.documents as RawStepDocument[]) || [];
  const supportingDocsList: RawStepDocument[] =
    (stepData['04-03-JUSTIFICATIFS']?.documents as RawStepDocument[]) || [];
  const appendSupportingDocsList: RawStepDocument[] =
    (stepData['04E-03-JUSTIFICATIFS']?.documents as RawStepDocument[]) || [];
  const voidCheck: RawStepDocument | undefined =
    stepData['05-01-PAIEMENT']?.voidCheck as RawStepDocument | undefined;

  // Per-screen entries tagged with the screen code they came from, so we can
  // resolve their `category` at mapping time without losing the upload origin.
  const draftDocsBySource: Array<{ doc: RawStepDocument; screenCode: string }> = [
    ...invoiceDocsList.map((doc) => ({ doc, screenCode: '04-01-FACTURES' })),
    ...appendInvoiceDocsList.map((doc) => ({ doc, screenCode: '04E-01-FACTURES' })),
    ...supportingDocsList.map((doc) => ({ doc, screenCode: '04-03-JUSTIFICATIFS' })),
    ...appendSupportingDocsList.map((doc) => ({ doc, screenCode: '04E-03-JUSTIFICATIFS' })),
    ...(voidCheck ? [{ doc: voidCheck, screenCode: '05-01-PAIEMENT' }] : []),
  ];

  // DEV-6958 — "Destination du voyage" display.
  //   Medical:   "{Pays} - {Ville}"  — country from 02M-02-VOYAGE.destination,
  //                                     city    from 03M-01-DETAILS.eventData.serviceLocationCity
  //   Non-medical: "{Pays}"          — country only (cancellation/baggage capture no city)
  //   Either half falls back to common.notAvailable (FR "N/D" / EN "N/A") when missing.
  const notAvailable = t('common.notAvailable');
  const lang = i18n.language === 'fr' ? 'fr' : 'en';

  const rawCountry = (travelData.destination || '').toString().trim();
  const countryLabel = rawCountry
    ? getCountryLabel(rawCountry as Country, lang)
    : notAvailable;

  const destinationDisplay = isMedical
    ? `${countryLabel} - ${eventData.serviceLocationCity || notAvailable}`
    : countryLabel;

  // Map expenses first
  const mappedExpenses = expensesList.map((expense: any, index: number) => {
    // For medical claims with coverage codes, translate the coverage code to current language
    // Otherwise, use the stored description
    let serviceLabel: string;
    if (expense.coverageCode) {
      serviceLabel = getCoverageCodeLabel(expense.coverageCode, i18n.language);
    } else {
      serviceLabel = expense.description || expense.service || expense.serviceProvider || 'N/A';
    }

    return {
      id: expense.id || String(index + 1),
      service: serviceLabel,
      serviceDate: expense.serviceStartDate || expense.serviceDate || expense.purchaseDate || expense.date || 'N/A',
      amount: expense.amountCAD || expense.amount || 0
    };
  });

  // Calculate subtotal from mapped expenses
  const expensesSubtotal = mappedExpenses.reduce((sum, expense) => sum + expense.amount, 0);

  // Build claim data from store
  const claim = {
    id: currentClaim?.id || claimId || 'N/A',
    caseNumber: eventNumber || currentClaim?.caseNumber || 'N/A',
    status: currentClaim?.status,
    currentScreenCode: currentClaim?.currentScreenCode,
    submittedAt: currentClaim?.submittedAt || currentClaim?.updatedAt || new Date(),
    totalAmountCAD: expensesSubtotal || totalAmount || calculatedTotal || currentClaim?.totalAmountCAD || 0,
    creditsAmount: creditsAmount,
    claimType: claimType,
    beneficiary: {
      firstName: firstBeneficiary.firstName || 'N/A',
      lastName: firstBeneficiary.lastName || 'N/A',
      dateOfBirth: firstBeneficiary.dateOfBirth || 'N/A'
    },
    travelDates: {
      departureDate: travelData.departureDate || travelData.plannedDepartureDate || 'N/A',
      returnDate: travelData.returnDate || travelData.plannedReturnDate || 'N/A'
    },
    eventDate: eventData.firstSymptomsDate || eventData.eventDate || eventData.eventKnownDate || eventData.incidentDate || 'N/A',
    destinationDisplay,
    description: eventData.reasonForHealthServices
        || eventData.medicalServicesDescription
        || eventData.eventDescription
        || t('claimDetails.noDescriptionProvided', 'No description provided'),
    expenses: mappedExpenses,
    // DEV-6896 — `SubmittedFileEntry[]` (typed shape consumed by ClaimDocumentsTab).
    // Field fallbacks preserved so legacy claim payloads still render correctly
    // (some draft docs only carry `fileName`/`fileType`/`mimeType`/`uploadDate`).
    documents: isClaimSubmitted
      ? submittedDocsList.map((doc, index) =>
          // BFF guarantees `category` on submitted claims; default to OTHER on a
          // legacy/partial payload that's missing it.
          toSubmittedFileEntry(doc, index, (doc.category as DocumentCategory) || 'OTHER'))
      : draftDocsBySource.map(({ doc, screenCode }, index) =>
          toSubmittedFileEntry(doc, index, categoryFromScreenCode(screenCode))),
    timeline: [
      ...(currentClaim?.createdAt ? [{
        id: '1',
        date: currentClaim.createdAt.toISOString(),
        title: t('claimDetails.timeline.created', 'Claim Created'),
        description: t('claimDetails.timeline.createdDesc', 'Claim draft created'),
        status: 'completed'
      }] : []),
      ...(currentClaim?.submittedAt ? [{
        id: '2',
        date: currentClaim.submittedAt.toISOString(),
        title: t('claimDetails.timeline.submitted', 'Claim Submitted'),
        description: t('claimDetails.timeline.submittedDesc', 'Your claim has been successfully submitted'),
        status: 'completed'
      }] : []),
      ...((currentClaim?.status === ClaimStatus.SUBMITTED || currentClaim?.status === ClaimStatus.TO_BE_COMPLETED) ? [{
        id: '3',
        date: null,
        title: t('claimDetails.timeline.review', 'Under Review'),
        description: t('claimDetails.timeline.reviewDesc', 'Claim is being reviewed by our team'),
        status: 'current'
      }] : []),
      ...((currentClaim?.status === ClaimStatus.COMPLETED) ? [{
        id: '4',
        date: currentClaim?.updatedAt?.toISOString() || null,
        title: t('claimDetails.timeline.decision', 'Decision'),
        description: t('claimDetails.timeline.approved', 'Claim approved'),
        status: 'completed'
      }] : []),
      ...((currentClaim?.status === ClaimStatus.CLOSED) ? [{
        id: '4',
        date: currentClaim?.updatedAt?.toISOString() || null,
        title: t('claimDetails.timeline.awaitingInformation', 'Awaiting Information'),
        description: t('claimDetails.timeline.awaitingInformationDesc', 'Additional information is required to process your claim'),
        status: 'current'
      }] : [])
    ]
  };

  // DEBUG: Log the constructed claim object
  const getStatusBadge = (status: ClaimStatus) => {
    // Normalize status to uppercase for consistent comparison
    const normalizedStatus = status?.toString().toUpperCase();

    switch (normalizedStatus) {
        // Envoyée (Submitted)
      case 'SUBMITTED':
      case 'NEW':
        return {
          text: i18n.language === 'fr' ? 'Envoyée' : 'Submitted',
          className: 'bg-blue-100 text-blue-800',
          icon: Send
        };

        // En cours d'analyse (Under Review)
      case 'IN_PROGRESS':
      case 'IN_REVISION':
      case 'REVISION_COMPLETED':
      case 'OPEN':
        return {
          text: i18n.language === 'fr' ? 'En cours d\'analyse' : 'Under Review',
          className: 'bg-yellow-100 text-yellow-800',
          icon: Eye
        };

        // Documents requis (Documents Required)
      case 'TO_BE_COMPLETED':
      case 'INFORMATION_REQUESTED':
        return {
          text: i18n.language === 'fr' ? 'Documents requis' : 'Documents Required',
          className: 'bg-orange-100 text-orange-800',
          icon: FileText
        };

        // Traitée (Processed)
      case 'COMPLETED':
        return {
          text: i18n.language === 'fr' ? 'Traitée' : 'Processed',
          className: 'bg-green-100 text-green-800',
          icon: CheckCircle
        };

        // En attente d'information (Awaiting Information) - NOVA external "Closed" (e.g. Missing Essential Information)
      case 'CLOSED':
        return {
          text: i18n.language === 'fr' ? 'En attente d\'information' : 'Awaiting Information',
          className: 'bg-orange-100 text-orange-800',
          icon: AlertCircle
        };

        // Annulée (Cancelled)
      case 'CANCELLED':
        return {
          text: i18n.language === 'fr' ? 'Annulée' : 'Cancelled',
          className: 'bg-red-100 text-red-800',
          icon: XCircle
        };

        // Draft (visible uniquement dans "Demandes en cours")
      case 'DRAFT':
        return {
          text: i18n.language === 'fr' ? 'Brouillon' : 'Draft',
          className: 'bg-gray-100 text-gray-800',
          icon: FileText
        };

      default:
        return {
          text: i18n.language === 'fr' ? 'Inconnu' : 'Unknown',
          className: 'bg-gray-100 text-gray-800',
          icon: AlertCircle
        };
    }
  };

  const statusBadge = getStatusBadge(claim.status);
  const StatusIcon = statusBadge.icon;

  // Load draft data when component mounts
  useEffect(() => {
    const loadClaimData = async () => {
      if (claimId) {
        setIsLoadingData(true);
        try {
          // Use getState() to avoid re-triggering effect when store updates
          await useClaimStore.getState().loadDraft(claimId);
        } catch (error) {
          logger.error('Failed to load draft', {
            errorType: 'LoadDraftError',
            screen: 'ClaimDetailsScreen'
          }, error as Error);
        } finally {
          setIsLoadingData(false);
        }
      } else {
        setIsLoadingData(false);
      }
    };
    loadClaimData();
  }, [claimId]); // Only depend on claimId, not loadDraft function

  // Determine if this is a draft claim (case-insensitive to handle API returning "Draft" instead of "draft")
  const isDraft = claim.status?.toString().toLowerCase() === 'draft';

  // append-expenses eligibility (MEDICAL only; hidden only for non-submitted states)
  const claimStatusUpper = claim.status?.toString().toUpperCase() ?? '';
  const APPEND_BLOCKED_STATUSES = ['DRAFT', 'CANCELLED', 'ABANDONED'];
  const canAppendExpenses = isMedical && !APPEND_BLOCKED_STATUSES.includes(claimStatusUpper);

  // append-document eligibility:
  // - medical: only when the analyst has flagged the claim as INFORMATION_REQUESTED
  //   (Case.ca_Internal_Status__c, surfaced as the displayed status — backend aliases
  //   AWAITING_ESSENTIAL_INFORMATION to INFORMATION_REQUESTED).
  // - cancellation/baggage: always available once the claim is sent, so the user can
  //   volunteer extra supporting documents anytime.
  // `isMedical` / `isNonMedical` hoisted above next to `claimType` (DEV-6958).
  const canAppendDocuments =
      (isMedical && claimStatusUpper === 'INFORMATION_REQUESTED') ||
      (isNonMedical && !APPEND_BLOCKED_STATUSES.includes(claimStatusUpper));

  // INFORMATION_REQUESTED → "analyst requested" wording. Any other status where the button
  // is visible (cancellation/baggage in IN_PROGRESS, COMPLETED, etc.) → generic "additional
  // documents" wording so the user is not told an analyst asked when none did.
  const isAnalystRequest = claimStatusUpper === 'INFORMATION_REQUESTED';

  const [isAppendEntryLoading, setIsAppendEntryLoading] = useState(false);
  const [isAppendDocumentsEntryLoading, setIsAppendDocumentsEntryLoading] = useState(false);

  const handleAppendExpensesEntry = async () => {
    if (!claim.id || isAppendEntryLoading) {
      return;
    }
    setIsAppendEntryLoading(true);
    try {
      await loadReferenceClaim(claim.id, currentClaim?.submissionId ?? undefined);
      navigate('/claim/06-01-NOUVEAUFRAIS');
    } catch (error) {
      logger.error('Failed to load reference claim for append flow', error);
      setIsAppendEntryLoading(false);
    }
  };

  const handleAppendDocumentsEntry = async () => {
    if (!claim.id || isAppendDocumentsEntryLoading) {
      return;
    }
    setIsAppendDocumentsEntryLoading(true);
    try {
      await loadReferenceClaim(claim.id, currentClaim?.submissionId ?? undefined);
      navigate('/claim/06-00-NOUVEAUDOCUMENT');
    } catch (error) {
      logger.error('Failed to load reference claim for append-document flow', error);
      setIsAppendDocumentsEntryLoading(false);
    }
  };
  const handleContinue = () => {
    // Navigate to the screen where the draft was saved
    // Use currentScreenCode from store (set by loadDraft)
    // If no currentScreenCode, fallback to first screen after legal notice
    const targetScreen = currentScreenCode || '01-00-DEMANDEUR';    navigate(`/claim/${targetScreen}`);
  };

  const handleDownloadDocument = async (doc: SubmittedFileEntry) => {
    if (!doc.documentKey) {
      logger.error('No documentKey found for document', {
        errorType: 'DocumentDownloadError',
        screen: 'ClaimDetailsScreen'
      });
      return;
    }

    // Show modal with downloading status
    setDownloadProgress({
      fileName: doc.name || 'document',
      status: 'downloading',
      message: ''
    });
    setShowDownloadModal(true);

    try {
      const blob = await downloadDocument(doc.documentKey);

      // Update to success status
      setDownloadProgress(prev => ({
        ...prev,
        status: 'success',
        message: t('claimDetails.download.success')
      }));

      // Wait a moment to show success message
      await new Promise(resolve => setTimeout(resolve, 500));

      // Create a download link
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = doc.name || 'document';

      // Trigger download
      document.body.appendChild(link);
      link.click();

      // Cleanup
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      // Close modal after a short delay
      setTimeout(() => {
        setShowDownloadModal(false);
      }, 1500);

    } catch (error: any) {
      logger.error('Error downloading document', {
        errorType: 'DocumentDownloadError',
        screen: 'ClaimDetailsScreen'
      }, error);

      // Update to error status
      setDownloadProgress(prev => ({
        ...prev,
        status: 'error',
        message: t('claimDetails.download.error')
      }));
    }
  };

  const handleAbandon = () => {
    setShowAbandonModal(true);
  };

  const confirmAbandon = async () => {
    setIsAbandoning(true);
    try {
      // Call cancelClaim from store (calls API endpoint POST /api/v1/claims/{eventId}/cancel)
      await useClaimStore.getState().cancelClaim();

      // Show success notification (optional - can be added via uiStore)
      // Navigate back to home
      navigate('/');
    } catch (error: any) {
      logger.error('Failed to abandon claim', {
        errorType: 'AbandonClaimError',
        screen: 'ClaimDetailsScreen'
      }, error);
      setIsAbandoning(false);
      setShowAbandonModal(false);
      // Show error notification (optional - can be added via uiStore)
      alert(i18n.language === 'fr'
          ? 'Erreur lors de l\'abandon de la demande. Veuillez réessayer.'
          : 'Error abandoning claim. Please try again.');
    }
  };

  const handleContest = () => {
    // Navigate to contest flow
    navigate(`/claim/contest/${claimId}`);
  };

  const handleSubmitDocuments = () => {
    // Navigate to document upload
    navigate(`/claim/04-03-JUSTIFICATIFS?claimId=${claimId}`);
  };

  const handleSubmitExpenses = () => {
    // Navigate to add expenses
    navigate(`/claim/04-01-FACTURES?claimId=${claimId}`);
  };

  const handleBack = () => {
    navigate('/');
  };

  const [eobDownloadingId, setEobDownloadingId] = useState<string | null>(null);
  const showError = useUIStore(state => state.showError);

  const handleEobDownload = async (documentKey: string, paymentId: string) => {
    setEobDownloadingId(paymentId);
    try {
      const blob = await downloadDocument(documentKey);
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `eob-${paymentId}.pdf`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch (error) {
      logger.error('Failed to download EOB document', { documentKey, error });
      showError(t('claimDetails.payments.downloadError'));
    } finally {
      setEobDownloadingId(null);
    }
  };

  // Scroll to top on mount
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  // Show loading spinner while data is being fetched
  if (isLoadingData) {
    return (
        <Layout>
          <div className="max-w-6xl mx-auto sm:px-6 lg:px-8 sm:py-6">
            <div className="flex flex-col items-center justify-center min-h-[400px]">
              <LoadingSpinner size="lg" />
              <p className="mt-4 text-gray-600">
                {i18n.language === 'fr' ? 'Chargement des détails de la réclamation...' : 'Loading claim details...'}
              </p>
            </div>
          </div>
        </Layout>
    );
  }

  // Append-expenses / append-document entry: fullscreen overlay matching the Suspense fallback
  // (App.tsx) so the transition into the lazy-loaded entry screen is visually seamless.
  if (isAppendEntryLoading || isAppendDocumentsEntryLoading) {
    return <LoadingSpinner size="lg" text="Chargement..." className="min-h-screen" />;
  }

  return (
      <Layout>
        <div className="max-w-6xl mx-auto sm:px-6 lg:px-8 sm:py-6">
          {/* Header */}
          <div className="mb-6">
            <Button
                variant="secondary"
                onClick={handleBack}
                className="mb-4"
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              {t('claimDetails.header.backToHome')}
            </Button>

            <div className="bg-white rounded-lg shadow-md p-6">
              <div className="flex flex-col md:flex-row md:items-center md:justify-between mb-4">
                <div>
                  <h1 className="text-3xl font-bold text-gray-900 mb-2">
                    {isDraft
                        ? t('claimDetails.header.draftClaim')
                        : t('claimDetails.header.claimDetails')}
                  </h1>
                  <p className="text-lg text-gray-600">
                    {t('claimDetails.header.caseNumber', { id: claim.caseNumber })}
                  </p>
                </div>
                <div className="mt-4 md:mt-0">
                <span className={`inline-flex items-center px-4 py-2 rounded-full text-sm font-medium ${statusBadge.className}`}>
                  <StatusIcon className="w-4 h-4 mr-2" />
                  {statusBadge.text}
                </span>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 pt-4 border-t border-gray-200">
                <div>
                  <p className="text-sm text-gray-600">{t('claimDetails.header.submissionDate')}</p>
                  <p className="text-base font-semibold text-gray-900">
                    {claim.submittedAt ? formatDateOrNA(claim.submittedAt.toISOString().split('T')[0], i18n.language === 'fr' ? 'fr-CA' : 'en-CA') : 'N/A'}
                  </p>
                </div>
                <div>
                  <p className="text-sm text-gray-600">{t('claimDetails.header.claimStatus')}</p>
                  <p className="text-base font-semibold text-gray-900">
                    {statusBadge.text}
                  </p>
                </div>
                <div>
                  <p className="text-sm text-gray-600">{t('claimDetails.header.totalAmount')}</p>
                  <p className="text-xl font-bold text-tenant-600">
                    ${(claim.totalAmountCAD - claim.creditsAmount).toFixed(2)} CAD
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Tabs */}
          <div className="bg-white rounded-lg shadow-md overflow-hidden mb-6">
            <div className="border-b border-gray-200">
              <nav className="flex -mb-px">
                <button
                    onClick={() => setActiveTab('overview')}
                    className={`flex-1 py-4 px-2 sm:px-6 text-center font-medium border-b-2 transition-colors ${
                        activeTab === 'overview'
                            ? 'border-tenant-500 text-tenant-600 bg-tenant-50'
                            : 'border-transparent text-gray-600 hover:text-gray-800 hover:border-gray-300'
                    }`}
                >
                  <FileText className="w-5 h-5 mx-auto sm:mb-1" />
                  <span className="hidden sm:block">{t('claimDetails.tabs.overview')}</span>
                </button>
                <button
                    onClick={() => setActiveTab('expenses')}
                    className={`flex-1 py-4 px-2 sm:px-6 text-center font-medium border-b-2 transition-colors ${
                        activeTab === 'expenses'
                            ? 'border-tenant-500 text-tenant-600 bg-tenant-50'
                            : 'border-transparent text-gray-600 hover:text-gray-800 hover:border-gray-300'
                    }`}
                >
                  <DollarSign className="w-5 h-5 mx-auto sm:mb-1" />
                  <span className="hidden sm:block">{t('claimDetails.tabs.expenses')}</span>
                </button>
                <button
                    onClick={() => setActiveTab('documents')}
                    className={`flex-1 py-4 px-2 sm:px-6 text-center font-medium border-b-2 transition-colors ${
                        activeTab === 'documents'
                            ? 'border-tenant-500 text-tenant-600 bg-tenant-50'
                            : 'border-transparent text-gray-600 hover:text-gray-800 hover:border-gray-300'
                    }`}
                >
                  <Upload className="w-5 h-5 mx-auto sm:mb-1" />
                  <span className="hidden sm:block">{t('claimDetails.tabs.documents')}</span>
                </button>
                <button
                    onClick={() => setActiveTab('timeline')}
                    className={`flex-1 py-4 px-2 sm:px-6 text-center font-medium border-b-2 transition-colors ${
                        activeTab === 'timeline'
                            ? 'border-tenant-500 text-tenant-600 bg-tenant-50'
                            : 'border-transparent text-gray-600 hover:text-gray-800 hover:border-gray-300'
                    }`}
                >
                  <Clock className="w-5 h-5 mx-auto sm:mb-1" />
                  <span className="hidden sm:block">{t('claimDetails.tabs.timeline')}</span>
                </button>
                <button
                    onClick={() => setActiveTab('payments')}
                    className={`flex-1 py-4 px-2 sm:px-6 text-center font-medium border-b-2 transition-colors ${
                        activeTab === 'payments'
                            ? 'border-tenant-500 text-tenant-600 bg-tenant-50'
                            : 'border-transparent text-gray-600 hover:text-gray-800 hover:border-gray-300'
                    }`}
                >
                  <DollarSign className="w-5 h-5 mx-auto sm:mb-1" />
                  <span className="hidden sm:block">{t('claimDetails.tabs.payments')}</span>
                </button>
              </nav>
            </div>

            {/* Tab Content */}
            <div className="p-6">
              {/* Overview Tab */}
              {activeTab === 'overview' && (
                  <div className="space-y-6">
                    <h3 className="text-lg font-semibold text-gray-900">
                      {t('claimDetails.overview.title')}
                    </h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      <div className="space-y-4">
                        <div>
                          <label className="text-sm font-medium text-gray-600">{t('claimDetails.type')}</label>
                          <p className="text-base text-gray-900">
                            {claim.claimType ? t(`progress.banner.claimTypes.${claimTypeLower}`) : t('common.notAvailable')}
                          </p>
                        </div>
                        <div>
                          <label className="text-sm font-medium text-gray-600">
                            {t('claimDetails.overview.beneficiary')}
                          </label>
                          <p className="text-base text-gray-900">
                            {claim.beneficiary.firstName} {claim.beneficiary.lastName}
                          </p>
                        </div>
                        <div>
                          <label className="text-sm font-medium text-gray-600">
                            {t('claimDetails.overview.travelDates')}
                          </label>
                          <p className="text-base text-gray-900">
                            {formatDateOrNA(claim.travelDates?.departureDate, i18n.language === 'fr' ? 'fr-CA' : 'en-CA')} - {formatDateOrNA(claim.travelDates?.returnDate, i18n.language === 'fr' ? 'fr-CA' : 'en-CA')}
                          </p>
                        </div>
                      </div>
                      <div className="space-y-4">
                        <div>
                          <label className="text-sm font-medium text-gray-600">
                            {t('claimDetails.overview.eventDate')}
                          </label>
                          <p className="text-base text-gray-900">
                            {formatDateOrNA(claim.eventDate, i18n.language === 'fr' ? 'fr-CA' : 'en-CA')}
                          </p>
                        </div>
                        <div>
                          <label className="text-sm font-medium text-gray-600">
                            {t('claimDetails.overview.travelDestination')}
                          </label>
                          <p className="text-base text-gray-900">
                            {claim.destinationDisplay}
                          </p>
                        </div>
                        <div>
                          <label className="text-sm font-medium text-gray-600">{t('claimDetails.description')}</label>
                          <p className="text-base text-gray-900 whitespace-pre-wrap break-words max-h-48 overflow-y-auto">
                            {claim.description}
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>
              )}

              {/* Expenses Tab */}
              {activeTab === 'expenses' && (
                  <PreviouslySubmittedExpenses
                      expenses={claim.expenses}
                      refundReceivedAmount={claim.creditsAmount}
                      title={t('claimDetails.expenses.listTitle')}
                      claimType={claimType}
                  />
              )}

              {/* Documents Tab — DEV-6896 grouped by category */}
              {activeTab === 'documents' && (
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900 mb-4">
                      {t('claimDetails.documents.uploadedDocuments')}
                    </h3>
                    <ClaimDocumentsTab
                        documentsList={claim.documents}
                        language={i18n.language}
                        onDownload={handleDownloadDocument}
                    />
                  </div>
              )}

              {/* Timeline Tab */}
              {activeTab === 'timeline' && (
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900 mb-6">
                      {t('claimDetails.timeline.timelineTitle')}
                    </h3>
                    <div className="space-y-6">
                      {claim.timeline.map((item, index) => (
                          <div key={item.id} className="flex">
                            <div className="flex flex-col items-center mr-4">
                              <div className={`flex items-center justify-center w-10 h-10 rounded-full ${
                                  item.status === 'completed' ? 'bg-green-100' :
                                      item.status === 'current' ? 'bg-tenant-100' : 'bg-gray-100'
                              }`}>
                                {item.status === 'completed' ? (
                                    <CheckCircle className="w-5 h-5 text-green-600" />
                                ) : item.status === 'current' ? (
                                    <Clock className="w-5 h-5 text-tenant-600" />
                                ) : (
                                    <div className="w-3 h-3 rounded-full bg-gray-300" />
                                )}
                              </div>
                              {index < claim.timeline.length - 1 && (
                                  <div className={`w-0.5 h-16 ${
                                      item.status === 'completed' ? 'bg-green-300' : 'bg-gray-300'
                                  }`} />
                              )}
                            </div>
                            <div className="flex-1 pb-8">
                              <div className="flex items-center justify-between mb-1">
                                <h4 className="text-base font-semibold text-gray-900">{item.title}</h4>
                                {item.date && (
                                    <span className="text-xs text-gray-500">
                              {(() => {
                                const date = new Date(item.date);
                                return !isNaN(date.getTime()) ? `${date.toLocaleDateString()} ${date.toLocaleTimeString()}` : 'N/A';
                              })()}
                            </span>
                                )}
                              </div>
                              <p className="text-sm text-gray-600">{item.description}</p>
                            </div>
                          </div>
                      ))}
                    </div>
                  </div>
              )}

              {/* Payments Tab */}
              {activeTab === 'payments' && (
                  <div>
                    <div className="mb-4">
                      <h3 className="text-lg font-semibold text-gray-900">
                        {t('claimDetails.payments.title')}
                      </h3>
                    </div>

                    {(!Array.isArray(paymentsData) || paymentsData.length === 0) ? (
                        <div className="flex items-center space-x-3 bg-blue-50 border border-blue-200 rounded-lg p-4">
                          <AlertCircle className="w-5 h-5 text-blue-500 flex-shrink-0" />
                          <p className="text-sm text-blue-700">{t('claimDetails.payments.noPayments')}</p>
                        </div>
                    ) : (
                        <>
                          {/* Mobile: Card View */}
                          <div className="block md:hidden space-y-4">
                            {paymentsData.map((payment: any, index: number) => {
                              const paymentId = payment.id || String(index + 1);
                              const serviceLabel = payment.coverageCode
                                  ? (getCoverageCodeLabel(payment.coverageCode, i18n.language) || payment.serviceDescription || payment.coverageCode)
                                  : (payment.serviceDescription || 'N/A');
                              const amount = `$${new Intl.NumberFormat('fr-CA', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(payment.reimbursementAmount || 0)}`;
                              const decisionBadge = getPaymentDecisionBadge(payment.decision);

                              return (
                                  <div key={paymentId} className="bg-white border border-gray-200 rounded-lg p-4 shadow-sm">
                                    <div className="space-y-2">
                                      <div>
                                        <p className="text-xs text-gray-500 uppercase">{t('claimDetails.payments.service')}</p>
                                        <p className="text-sm font-medium text-gray-900 break-words">{serviceLabel}</p>
                                      </div>
                                      <div>
                                        <p className="text-xs text-gray-500 uppercase">{t('claimDetails.payments.serviceDate')}</p>
                                        <p className="text-sm text-gray-900">
                                          {payment.serviceDate || '—'}
                                        </p>
                                      </div>
                                      <div>
                                        <p className="text-xs text-gray-500 uppercase">{t('claimDetails.payments.decision')}</p>
                                        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${decisionBadge.className}`}>
                                          {t(decisionBadge.labelKey)}
                                        </span>
                                      </div>
                                      <div className="flex justify-between items-center pt-2 border-t border-gray-100">
                                        <div>
                                          <p className="text-xs text-gray-500 uppercase">{t('claimDetails.payments.reimbursement')}</p>
                                          <p className="text-base font-bold text-gray-900">{amount} {t('claimDetails.payments.amountFormat')}</p>
                                        </div>
                                        <div className="text-right">
                                          <p className="text-xs text-gray-500 uppercase">{t('claimDetails.payments.paymentDate')}</p>
                                          <p className="text-sm text-gray-900">
                                            {payment.paymentDate || '—'}
                                          </p>
                                        </div>
                                      </div>
                                      {eobDisplayEnabled && (
                                        <div className="pt-2 border-t border-gray-100">
                                          <p className="text-xs text-gray-500 uppercase">{t('claimDetails.payments.eob')}</p>
                                          {payment.eobDocumentKey ? (
                                              <Button
                                                  variant="secondary"
                                                  size="sm"
                                                  onClick={() => handleEobDownload(payment.eobDocumentKey, paymentId)}
                                                  disabled={eobDownloadingId === paymentId}
                                                  className="mt-1"
                                              >
                                                {eobDownloadingId === paymentId ? (
                                                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                                                ) : (
                                                  <Download className="w-4 h-4 mr-2" />
                                                )}
                                                {t('claimDetails.payments.downloadEob')}
                                              </Button>
                                          ) : (
                                              <p className="text-xs text-gray-500 italic mt-1">{t('claimDetails.payments.eobNotAvailable')}</p>
                                          )}
                                        </div>
                                      )}
                                    </div>
                                  </div>
                              );
                            })}
                          </div>

                          {/* Desktop: Table View */}
                          <div className="hidden md:block overflow-x-auto">
                            <table className="min-w-full divide-y divide-gray-200">
                              <thead className="bg-gray-50">
                              <tr>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                  {t('claimDetails.payments.service')}
                                </th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                  {t('claimDetails.payments.serviceDate')}
                                </th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                  {t('claimDetails.payments.decision')}
                                </th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                  {t('claimDetails.payments.reimbursement')}
                                </th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                  {t('claimDetails.payments.paymentDate')}
                                </th>
                                {eobDisplayEnabled && (
                                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                    {t('claimDetails.payments.eob')}
                                  </th>
                                )}
                              </tr>
                              </thead>
                              <tbody className="bg-white divide-y divide-gray-200">
                              {paymentsData.map((payment: any, index: number) => {
                                const paymentId = payment.id || String(index + 1);
                                const serviceLabel = payment.coverageCode
                                    ? (getCoverageCodeLabel(payment.coverageCode, i18n.language) || payment.serviceDescription || payment.coverageCode)
                                    : (payment.serviceDescription || 'N/A');
                                const amount = `$${new Intl.NumberFormat('fr-CA', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(payment.reimbursementAmount || 0)}`;
                                const decisionBadge = getPaymentDecisionBadge(payment.decision);

                                return (
                                    <tr key={paymentId}>
                                      <td className="px-6 py-4 text-sm text-gray-900">
                                        {serviceLabel}
                                      </td>
                                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                                        {payment.serviceDate || '—'}
                                      </td>
                                      <td className="px-6 py-4 whitespace-nowrap text-sm">
                                        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${decisionBadge.className}`}>
                                          {t(decisionBadge.labelKey)}
                                        </span>
                                      </td>
                                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                                        {amount} {t('claimDetails.payments.amountFormat')}
                                      </td>
                                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                                        {payment.paymentDate || '—'}
                                      </td>
                                      {eobDisplayEnabled && (
                                        <td className="px-6 py-4 whitespace-nowrap text-sm">
                                          {payment.eobDocumentKey ? (
                                              <Button
                                                  variant="secondary"
                                                  size="sm"
                                                  onClick={() => handleEobDownload(payment.eobDocumentKey, paymentId)}
                                                  disabled={eobDownloadingId === paymentId}
                                              >
                                                {eobDownloadingId === paymentId ? (
                                                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                                                ) : (
                                                  <Download className="w-4 h-4 mr-2" />
                                                )}
                                                {t('claimDetails.payments.downloadEob')}
                                              </Button>
                                          ) : (
                                              <p className="text-xs text-gray-500 italic">{t('claimDetails.payments.eobNotAvailable')}</p>
                                          )}
                                        </td>
                                      )}
                                    </tr>
                                );
                              })}
                              </tbody>
                            </table>
                          </div>
                        </>
                    )}
                  </div>
              )}
            </div>
          </div>

          {/* Action Buttons - Only show for draft claims */}
          {isDraft && (
              <div className="bg-white rounded-lg shadow-md p-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-4">
                  {t('claimDetails.actions.title')}
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <Button
                      variant="primary"
                      onClick={handleContinue}
                      className="w-full"
                  >
                    <Play className="w-4 h-4 mr-2" />
                    {t('claimDetails.actions.continueClaim')}
                  </Button>
                  <Button
                      variant="danger"
                      onClick={handleAbandon}
                      className="w-full"
                  >
                    <Trash2 className="w-4 h-4 mr-2" />
                    {t('claimDetails.actions.abandonClaim')}
                  </Button>
                </div>
              </div>
          )}

          {/* Append Expenses entry — append-expenses flow
              Visible only for submitted MEDICAL claims in an append-eligible status. */}
          {canAppendExpenses && (
              <div className="bg-white rounded-lg shadow-md p-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-4">
                  {t('appendExpenses.entry.sectionTitle', 'Nouveaux frais à soumettre ?')}
                </h3>
                <p className="text-sm text-gray-600 mb-4">
                  {t(
                    'appendExpenses.entry.description',
                    'Ajoutez de nouveaux frais à cette réclamation existante sans recréer de nouveau dossier.'
                  )}
                </p>
                <Button
                    variant="primary"
                    onClick={handleAppendExpensesEntry}
                    disabled={isAppendEntryLoading}
                    className="w-full md:w-auto"
                >
                  {t('appendExpenses.entry.button', 'Soumettre de nouveaux frais pour cette réclamation')}
                </Button>
              </div>
          )}

          {/* Append Documents entry — append-document flow.
              Visible only when the analyst has flagged the claim as INFORMATION_REQUESTED
              (i.e., they emailed the user asking for additional documents). All flow types
              are eligible. Coexists with the Append Expenses entry in the rare case both
              would apply (today: append-expenses excludes INFORMATION_REQUESTED, so the two
              never overlap). */}
          {canAppendDocuments && (
              <div className="bg-white rounded-lg shadow-md p-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-4">
                  {isAnalystRequest
                      ? t('appendDocuments.entry.sectionTitle', 'Nouveaux documents demandés ?')
                      : t('appendDocuments.entry.additionalSectionTitle', 'Documents supplémentaires')}
                </h3>
                <p className="text-sm text-gray-600 mb-4">
                  {isAnalystRequest
                      ? t(
                          'appendDocuments.entry.description',
                          "Ajoutez les documents demandés par l'analyste pour compléter cette réclamation."
                        )
                      : t(
                          'appendDocuments.entry.additionalDescription',
                          'Vous avez des documents supplémentaires à joindre à cette réclamation ?'
                        )}
                </p>
                <Button
                    variant="primary"
                    onClick={handleAppendDocumentsEntry}
                    disabled={isAppendDocumentsEntryLoading}
                    className="w-full md:w-auto"
                >
                  {t('appendDocuments.entry.button', 'Soumettre de nouveaux documents pour cette réclamation')}
                </Button>
              </div>
          )}
        </div>

        {/* Abandon Claim Confirmation Modal */}
        <Modal
            isOpen={showAbandonModal}
            onClose={() => setShowAbandonModal(false)}
            title={i18n.language === 'fr' ? 'Abandonner la demande' : 'Abandon Claim'}
            size="md"
            footer={
              <>
                <Button
                    variant="secondary"
                    onClick={() => setShowAbandonModal(false)}
                    disabled={isAbandoning}
                >
                  {i18n.language === 'fr' ? 'Annuler' : 'Cancel'}
                </Button>
                <Button
                    variant="danger"
                    onClick={confirmAbandon}
                    disabled={isAbandoning}
                >
                  {isAbandoning ? (
                      <>
                        <LoadingSpinner size="sm" />
                        <span className="ml-2">
                    {i18n.language === 'fr' ? 'Abandon en cours...' : 'Abandoning...'}
                  </span>
                      </>
                  ) : (
                      <>
                        <Trash2 className="w-4 h-4 mr-2" />
                        {i18n.language === 'fr' ? 'Oui, abandonner' : 'Yes, Abandon'}
                      </>
                  )}
                </Button>
              </>
            }
        >
          <div className="space-y-4">
            <div className="flex items-start space-x-3">
              <AlertCircle className="w-6 h-6 text-warning-500 flex-shrink-0 mt-1" />
              <div>
                <p className="text-gray-700 mb-2">
                  {i18n.language === 'fr'
                      ? 'Êtes-vous sûr de vouloir abandonner cette demande de réclamation?'
                      : 'Are you sure you want to abandon this claim?'}
                </p>
                <p className="text-gray-600 text-sm">
                  {i18n.language === 'fr'
                      ? 'Cette action changera le statut de votre demande à "Annulée". Vous pourrez encore consulter cette demande dans votre historique pendant 3 mois.'
                      : 'This action will change your claim status to "Cancelled". You will still be able to view this claim in your history for 3 months.'}
                </p>
              </div>
            </div>
          </div>
        </Modal>

        {/* Download Progress Modal */}
        <Modal
            isOpen={showDownloadModal}
            onClose={() => downloadProgress.status !== 'downloading' && setShowDownloadModal(false)}
            title={t('claimDetails.download.modalTitle')}
            size="md"
            footer={
              downloadProgress.status !== 'downloading' ? (
                  <Button
                      variant="primary"
                      onClick={() => setShowDownloadModal(false)}
                  >
                    {t('common.close')}
                  </Button>
              ) : null
            }
        >
          <div className="space-y-4">
            <div className="flex flex-col items-center justify-center py-6">
              {/* Downloading state */}
              {downloadProgress.status === 'downloading' && (
                  <>
                    <div className="relative">
                      <LoadingSpinner size="lg" />
                    </div>
                    <p className="mt-4 text-base font-medium text-gray-900">
                      {t('claimDetails.download.downloading')}
                    </p>
                    <p className="mt-2 text-sm text-gray-600 text-center break-words max-w-md">
                      {downloadProgress.fileName}
                    </p>
                  </>
              )}

              {/* Success state */}
              {downloadProgress.status === 'success' && (
                  <>
                    <div className="flex items-center justify-center w-16 h-16 rounded-full bg-green-100">
                      <CheckCircle className="w-10 h-10 text-green-600" />
                    </div>
                    <p className="mt-4 text-base font-medium text-gray-900">
                      {downloadProgress.message}
                    </p>
                    <p className="mt-2 text-sm text-gray-600 text-center break-words max-w-md">
                      {downloadProgress.fileName}
                    </p>
                  </>
              )}

              {/* Error state */}
              {downloadProgress.status === 'error' && (
                  <>
                    <div className="flex items-center justify-center w-16 h-16 rounded-full bg-red-100">
                      <XCircle className="w-10 h-10 text-red-600" />
                    </div>
                    <p className="mt-4 text-base font-medium text-red-900">
                      {downloadProgress.message}
                    </p>
                    <p className="mt-2 text-sm text-gray-600 text-center break-words max-w-md">
                      {downloadProgress.fileName}
                    </p>
                    <p className="mt-2 text-xs text-gray-500">
                      {t('claimDetails.download.tryAgain')}
                    </p>
                  </>
              )}
            </div>
          </div>
        </Modal>
      </Layout>
  );
};

export default ClaimDetailsScreen;
